import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setOnboardingStep } from '../../../services/apiClient';

const STEP_KEYS = [
  'customers_json_registered',
  'gitlab_ci_vars_created',
  'compose_files_edited',
  'volume_chowned',
  'etl_container_verified',
];

/**
 * Fixed, non-sequential checklist of the manual infra steps a real tenant
 * onboarding still requires (Phase 5, Section 5) -- rendered as plain
 * checkboxes, not a numbered stepper, because these 5 steps have no
 * required order.
 */
const OnboardingTab = ({ customerId, customer, onSaved }) => {
  const { t } = useTranslation('admin');
  const [notes, setNotes] = useState({});
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const initial = {};
    STEP_KEYS.forEach((step) => {
      initial[step] = customer?.onboarding_status?.[step]?.note || '';
    });
    setNotes(initial);
  }, [customerId]);

  const status = customer?.onboarding_status || {};

  const handleToggle = async (step, done) => {
    setErr(null);
    try {
      const updated = await setOnboardingStep(customerId, step, done, notes[step]);
      onSaved(updated.onboarding_status);
    } catch (e) {
      setErr(t('errors.updateOnboardingStep', { detail: e.message }));
    }
  };

  const handleNoteBlur = async (step) => {
    const currentlyDone = status[step]?.done || false;
    setErr(null);
    try {
      const updated = await setOnboardingStep(customerId, step, currentlyDone, notes[step]);
      onSaved(updated.onboarding_status);
    } catch (e) {
      setErr(t('errors.updateOnboardingStep', { detail: e.message }));
    }
  };

  const toggleExpanded = (step) => {
    setExpanded((prev) => ({ ...prev, [step]: !prev[step] }));
  };

  return (
    <div className="flex-1">
      <h3 className="text-sm font-medium text-gray-700 mb-1">{t('onboarding.title')}</h3>
      <p className="text-xs text-gray-500 mb-3">{t('onboarding.intro')}</p>
      {err && <div className="mb-2 text-red-600 text-sm">{err}</div>}
      <ul className="space-y-2">
        {STEP_KEYS.map((step) => {
          const stepStatus = status[step] || { done: false, note: '', updated_at: null };
          const isExpanded = !!expanded[step];
          return (
            <li key={step} className="border rounded p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={stepStatus.done}
                  onChange={(e) => handleToggle(step, e.target.checked)}
                />
                <span className="flex-1">
                  <span className={stepStatus.done ? 'text-emerald-700' : 'text-gray-700'}>
                    {t(`onboarding.steps.${step}.summary`)}
                  </span>
                  {stepStatus.updated_at && (
                    <span className="block text-xs text-gray-400">
                      {t('onboarding.updatedAt', { when: stepStatus.updated_at })}
                    </span>
                  )}
                  <input
                    type="text"
                    className="mt-1 w-full text-sm border rounded px-2 py-1"
                    placeholder={t('onboarding.notePlaceholder')}
                    value={notes[step] ?? ''}
                    onChange={(e) => setNotes((prev) => ({ ...prev, [step]: e.target.value }))}
                    onBlur={() => handleNoteBlur(step)}
                  />
                </span>
              </label>
              <button
                type="button"
                className="mt-2 text-xs text-blue-600 hover:underline"
                onClick={() => toggleExpanded(step)}
                aria-expanded={isExpanded}
              >
                {isExpanded ? t('onboarding.hideDetails') : t('onboarding.showDetails')}
              </button>
              {isExpanded && (
                <ol className="mt-2 ml-5 list-decimal space-y-1.5 text-xs text-gray-600">
                  {t(`onboarding.steps.${step}.details`, { returnObjects: true }).map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ol>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default OnboardingTab;
